import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import config from './config';
import { getOrganizationContext, getOrganizationHeaders } from './orgContext';
import { AdminBreadcrumbs } from './admin/components/AdminUiPrimitives';
import { AdminPageHeader } from './admin/components/AdminMasterDetail';
import SessionHub, { SessionHubData } from './components/sessions/SessionHub';

const API_BASE_URL = `${config.apiBaseUrl}/api`;

const MySessionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const sessionId = Number(id ?? 0);
  const navigate = useNavigate();
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();

  const [session, setSession] = useState<SessionHubData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [joinMessage, setJoinMessage] = useState<string | null>(null);

  const orgContext = getOrganizationContext();
  const scheduleBase = orgContext?.organizationSlug
    ? `/${orgContext.organizationSlug}/my-schedule`
    : '/my-schedule';

  const currentUserName = useMemo(() => String(user?.name || user?.nickname || user?.email || 'Current user'), [user]);

  const loadSession = useCallback(async () => {
    if (!isAuthenticated || !Number.isFinite(sessionId) || sessionId <= 0) {
      setError('No valid session selected.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/schedules/sessions/${sessionId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...getOrganizationHeaders(),
        },
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || `Failed to load session: ${response.statusText}`);
      }

      const data = await response.json();
      setSession(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load session.');
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, isAuthenticated, sessionId]);

  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  const handleJoin = async () => {
    if (!session) {
      return;
    }

    setJoining(true);
    setJoinMessage(null);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/schedules/sessions/${session.id}/join`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...getOrganizationHeaders(),
        },
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || `Failed to join session: ${response.statusText}`);
      }

      const data = await response.json();
      setJoinMessage(data?.alreadyJoined
        ? 'You were already marked as joined. Opening the meeting now.'
        : 'You have been marked as joined. Opening the meeting now.');

      if (data?.meetingUrl) {
        window.open(data.meetingUrl, '_blank', 'noopener,noreferrer');
      }

      await loadSession();
    } catch (err: any) {
      setJoinMessage(err?.message || 'Failed to join session.');
    } finally {
      setJoining(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-bold text-gray-800 mb-2">Sign in required</h2>
        <p className="text-gray-500">Please sign in to view your session.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <AdminBreadcrumbs items={[
        { label: 'Dashboard', to: '/analytics' },
        { label: 'My Schedule', to: scheduleBase },
        { label: 'Session Hub', isCurrent: true },
      ]} />

      <AdminPageHeader
        title={session ? (session.title || `Session ${session.id}`) : 'Session Hub'}
        subtitle={session ? `${session.courseName || session.classroomName} • ${session.teacherDisplayName}` : 'Join your session and keep the hub open while you meet'}
        actions={[
          { key: 'back', label: 'Back to schedule', variant: 'secondary', onClick: () => navigate(scheduleBase) },
        ]}
      />

      {loading ? (
        <div style={{ borderRadius: 12, background: '#fff', border: '1px solid #dfe8f8', padding: 18, color: '#475569' }}>
          Loading session details...
        </div>
      ) : error ? (
        <div style={{ borderRadius: 12, border: '1px solid #fecaca', background: '#fff1f2', color: '#b91c1c', padding: 18 }}>
          {error}
        </div>
      ) : session ? (
        <SessionHub
          session={session}
          currentUserName={currentUserName}
          currentUserKey={String(user?.sub || user?.email || currentUserName)}
          getDiscussionAccessToken={getAccessTokenSilently}
          getDiscussionHeaders={async () => {
            const token = await getAccessTokenSilently();
            return {
              Authorization: `Bearer ${token}`,
              ...getOrganizationHeaders(),
            };
          }}
          joining={joining}
          joinMessage={joinMessage}
          onJoin={handleJoin}
        />
      ) : null}
    </div>
  );
};

export default MySessionPage;
